import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  Check,
  CheckCheck,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  Info,
  Play,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  TrendingDown,
  TrendingUp,
  X,
  EyeOff,
} from 'lucide-react';
import {
  applyWholesalerPrice,
  applyBulkWholesalerPrices,
  fetchB2BReconciliation,
  triggerPortalCrawl,
  searchB2BWholesalerOffers,
  matchB2BProductOffer,
  unlinkB2BProductOffer,
  hideProductOnWooCommerce,
  type B2BReconciliationItemDto,
} from '../api.js';
import { Alert, Button, Loading, PageHeader, Panel, PanelBody, cn } from '../ui/index.js';

function formatTnd(millimes: number | null): string {
  if (millimes === null || !Number.isFinite(millimes)) return '—';
  return (millimes / 1000).toFixed(3) + ' TND';
}

export function B2BPriceVatMatchingPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const initialQ = searchParams.get('q') ?? '';
  const initialBrand = searchParams.get('brand') ?? '';
  const initialVatOnly = searchParams.get('vatOnly') === 'true';
  const initialHasOffers = searchParams.get('hasOffers') === 'true';
  const initialMissingPrice = searchParams.get('missingPrice') === 'true';
  const initialApprovedOnly =
    searchParams.get('approvedOnly') === 'true' || searchParams.get('filter') === 'approved';
  const initialSort =
    (searchParams.get('sortBy') as 'recent_approved' | 'missing_price' | 'name' | 'id') ||
    (initialApprovedOnly ? 'recent_approved' : 'recent_approved');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(initialQ);
  const [brand, setBrand] = useState(initialBrand);
  const [vatOnly, setVatOnly] = useState(initialVatOnly);
  const [hasOffersOnly, setHasOffersOnly] = useState(initialHasOffers);
  const [missingPriceOnly, setMissingPriceOnly] = useState(initialMissingPrice);
  const [approvedOnly, setApprovedOnly] = useState(initialApprovedOnly);
  const [sortBy, setSortBy] = useState<'recent_approved' | 'missing_price' | 'name' | 'id'>(initialSort);
  const [statusMsg, setStatusMsg] = useState<{ text: string; tone: 'info' | 'alarm' } | null>(null);

  // Manual match modal state
  const [manualMatchTarget, setManualMatchTarget] = useState<B2BReconciliationItemDto | null>(null);
  const [manualSearchQuery, setManualSearchQuery] = useState('');
  const [manualSearchSource, setManualSearchSource] = useState('all');

  const openManualMatch = (item: B2BReconciliationItemDto) => {
    setManualMatchTarget(item);
    setManualSearchQuery(item.product.name.trim());
    setManualSearchSource('all');
  };

  const searchChips = useMemo(() => {
    if (!manualMatchTarget) return [];
    const chips: string[] = [];
    const p = manualMatchTarget.product;
    if (p.name) chips.push(p.name);
    if (p.brand && !chips.includes(p.brand)) chips.push(p.brand);
    const words = p.name.split(/\s+/).filter((w) => w.length > 2);
    if (words.length >= 2) {
      const twoWords = words.slice(0, 2).join(' ');
      if (!chips.includes(twoWords)) chips.push(twoWords);
      if (words.length >= 3) {
        const threeWords = words.slice(0, 3).join(' ');
        if (!chips.includes(threeWords)) chips.push(threeWords);
      }
    }
    if (p.sku && p.sku.length > 2 && !chips.includes(p.sku)) chips.push(p.sku);
    if (p.gtin && !chips.includes(p.gtin)) chips.push(p.gtin);
    return chips;
  }, [manualMatchTarget]);

  const manualSearchQueryResults = useQuery({
    queryKey: [
      'b2b-wholesaler-search',
      manualSearchQuery,
      manualSearchSource,
      manualMatchTarget?.product.id,
    ],
    queryFn: () =>
      searchB2BWholesalerOffers({
        q: manualSearchQuery,
        productId: manualMatchTarget?.product.id,
        sourceKey: manualSearchSource !== 'all' ? manualSearchSource : undefined,
        limit: 50,
      }),
    enabled: Boolean(manualMatchTarget && manualSearchQuery.trim().length >= 2),
  });

  const matchOfferMutation = useMutation({
    mutationFn: (input: { productId: number; offerId: number; adoptPrice?: boolean; adoptVat?: boolean }) =>
      matchB2BProductOffer(input.productId, input),
    onSuccess: (_data, variables) => {
      setStatusMsg({
        text: variables.adoptPrice
          ? "✓ Offre liée au produit et prix d'achat/TVA adoptés avec succès."
          : '✓ Offre liée avec succès au produit.',
        tone: 'info',
      });
      void queryClient.invalidateQueries({ queryKey: ['b2b-reconciliation'] });
      void queryClient.invalidateQueries({ queryKey: ['b2b-wholesaler-search'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
    },
    onError: (err) => {
      setStatusMsg({ text: `Erreur de liaison : ${String(err)}`, tone: 'alarm' });
    },
  });

  const unlinkOfferMutation = useMutation({
    mutationFn: (input: { productId: number; offerId: number }) =>
      unlinkB2BProductOffer(input.productId, input.offerId),
    onSuccess: () => {
      setStatusMsg({ text: '✓ Offre déliée avec succès.', tone: 'info' });
      void queryClient.invalidateQueries({ queryKey: ['b2b-reconciliation'] });
      void queryClient.invalidateQueries({ queryKey: ['b2b-wholesaler-search'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
    },
    onError: (err) => {
      setStatusMsg({ text: `Erreur de déliaison : ${String(err)}`, tone: 'alarm' });
    },
  });

  const reconciliationQuery = useQuery({
    queryKey: [
      'b2b-reconciliation',
      page,
      search,
      brand,
      vatOnly,
      hasOffersOnly,
      missingPriceOnly,
      approvedOnly,
      sortBy,
    ],
    queryFn: () =>
      fetchB2BReconciliation({
        page,
        limit: 30,
        q: search || undefined,
        brand: brand || undefined,
        vatDiscrepancyOnly: vatOnly,
        hasWholesalerOffer: hasOffersOnly,
        missingPurchasePriceOnly: missingPriceOnly,
        eanApprovedOnly: approvedOnly,
        sortBy,
      }),
  });

  const adoptPriceMutation = useMutation({
    mutationFn: (input: { productId: number; offerId?: number; priceHt?: number; sourceKey?: string }) =>
      applyWholesalerPrice(input.productId, input),
    onSuccess: (data) => {
      setStatusMsg({
        text: `Prix d'achat mis à jour avec succès : ${(data.priceHt / 1000).toFixed(3)} TND.`,
        tone: 'info',
      });
      void queryClient.invalidateQueries({ queryKey: ['b2b-reconciliation'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
    },
    onError: (err) => {
      setStatusMsg({ text: `Erreur d'adoption : ${String(err)}`, tone: 'alarm' });
    },
  });

  const adoptBulkMutation = useMutation({
    mutationFn: (
      bulkItems: Array<{
        productId: number;
        offerId?: number;
        priceHt?: number;
        sourceKey?: string;
        vatRate?: number;
      }>
    ) => applyBulkWholesalerPrices(bulkItems),
    onSuccess: (data) => {
      setStatusMsg({
        text: `⚡ ${data.appliedCount} prix d'achat grossistes ont été adoptés avec succès.`,
        tone: 'info',
      });
      void queryClient.invalidateQueries({ queryKey: ['b2b-reconciliation'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
    },
    onError: (err) => {
      setStatusMsg({ text: `Erreur d'adoption en lot : ${String(err)}`, tone: 'alarm' });
    },
  });

  const crawlMutation = useMutation({
    mutationFn: (portalKey: string) => triggerPortalCrawl(portalKey),
    onSuccess: (data) => {
      setStatusMsg({
        text: `Crawl pour le portail grossiste "${data.portalKey}" planifié avec succès (Job #${data.jobId ?? 'OK'}).`,
        tone: 'info',
      });
      void queryClient.invalidateQueries({ queryKey: ['b2b-reconciliation'] });
    },
    onError: (err) => {
      setStatusMsg({ text: `Erreur lancement crawl : ${String(err)}`, tone: 'alarm' });
    },
  });

  const hideMutation = useMutation({
    mutationFn: (productId: number) => hideProductOnWooCommerce(productId),
    onSuccess: (_data, productId) => {
      setStatusMsg({
        text: `✓ Produit #${productId} masqué avec succès du catalogue et retiré du site web WooCommerce (catalog_visibility: hidden).`,
        tone: 'info',
      });
      void queryClient.invalidateQueries({ queryKey: ['b2b-reconciliation'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match'] });
      void queryClient.invalidateQueries({ queryKey: ['final-ean-match-kpis'] });
      void queryClient.invalidateQueries({ queryKey: ['lifecycle-search'] });
      void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
    },
    onError: (err) => {
      setStatusMsg({
        text: `Erreur lors du masquage : ${err instanceof Error ? err.message : String(err)}`,
        tone: 'alarm',
      });
    },
  });

  const handleHideProduct = (productId: number, productName: string) => {
    if (
      window.confirm(
        `Masquer le produit #${productId} "${productName}" ?\n\n` +
          `• Le produit sera marqué "Masqué" / Discontinué.\n` +
          `• La visibilité sur WooCommerce passera à "hidden" (retiré du site web).\n` +
          `• Il disparaîtra immédiatement de la liste de réconciliation B2B.`,
      )
    ) {
      hideMutation.mutate(productId);
    }
  };

  const data = reconciliationQuery.data;
  const items = data?.items ?? [];
  const stats = data?.stats;
  const pagination = data?.pagination;

  const adoptableItems = useMemo(() => {
    return items.filter((row) => {
      if (!row.buyBox) return false;
      const targetPrice = row.buyBox.unitPriceRealHt ?? row.buyBox.priceHt;
      return targetPrice !== row.product.currentPurchasePriceHt;
    });
  }, [items]);

  return (
    <div className="space-y-5">
      <PageHeader
        index="S19"
        title="Rapprochement Prix & TVA Grossistes"
        purpose="Comparatif par EAN entre le catalogue MaPara et les 5 portails grossistes B2B (Santé Access, Omnipharm, Medigros, KIPROPHA, ParaConnect) + Fichiers Déposants. Affiche exclusivement les produits ayant reçu la double validation (Couche 1 fil d'attente + Couche 2 contrôle visuel Final EAN Match) afin de garantir une exactitude barcode à 100%."
        meta={[
          { label: 'Produits Couche 2', value: String(stats?.totalProducts ?? '—') },
          { label: 'Sans prix d\'achat', value: String(stats?.missingPurchasePriceCount ?? '—') },
          { label: 'Matchés grossistes', value: String(stats?.matchedWithWholesalerCount ?? '—') },
          { label: 'En attente Couche 2', value: String(stats?.pendingLayer2Count ?? '—') },
          { label: 'Anomalies TVA', value: String(stats?.vatDiscrepanciesCount ?? '0') },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/final-ean-match"
              className="inline-flex items-center gap-1.5 rounded-lg border border-herb-600/30 bg-white hover:bg-herb-50 px-3 py-1.5 text-xs font-bold text-herb-800 shadow-2xs transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5 text-herb-700" />
              Final EAN Match (S23)
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void queryClient.invalidateQueries({ queryKey: ['b2b-reconciliation'] })}
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Actualiser
            </Button>
          </div>
        }
      />

      {/* Layer 2 Double-Validation Guarantee Banner */}
      <div className="rounded-xl border border-emerald-300 bg-emerald-50/70 p-3.5 text-xs text-emerald-950 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 shadow-2xs">
        <div className="flex items-center gap-2.5">
          <div className="rounded-full bg-emerald-600 text-white p-1 shrink-0">
            <CheckCheck className="h-4 w-4" />
          </div>
          <div>
            <strong>Garantie Double Validation (Couche 2) :</strong> Seuls les produits confirmés visuellement dans{' '}
            <Link to="/final-ean-match" className="underline font-bold text-emerald-900 hover:text-emerald-700">
              Final EAN Match
            </Link>{' '}
            apparaissent sur cet écran. La réconciliation des prix d'achat avec les grossistes s'effectue sur des codes-barres 100% vérifiés.
          </div>
        </div>
        {stats?.pendingLayer2Count !== undefined && stats.pendingLayer2Count > 0 && (
          <Link
            to="/final-ean-match?matchFilter=pending_layer2"
            className="inline-flex items-center gap-1 shrink-0 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-bold px-3 py-1.5 text-xs transition-colors shadow-2xs"
          >
            {stats.pendingLayer2Count} en attente Couche 2 →
          </Link>
        )}
      </div>

      {statusMsg && (
        <Alert tone={statusMsg.tone} title="Opération grossiste">
          {statusMsg.text}
        </Alert>
      )}

      {/* Portals Status Strip & On-demand Triggers */}
      <Panel>
        <PanelBody className="py-3 px-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-ink-ghost">
              <Building2 className="h-4 w-4 text-herb-700" />
              Sondes & Crawlers Grossistes B2B (24h)
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { key: 'omnipharm', label: 'Omnipharm' },
                { key: 'santeaccess', label: 'Santé Access' },
                { key: 'medigros', label: 'Medigros' },
                { key: 'kilani', label: 'Kipropha' },
                { key: 'paraconnect', label: 'ParaConnect' },
              ].map((portal) => {
                const info = stats?.portals.find((p) => p.key === portal.key);
                const isRunning = crawlMutation.isPending && crawlMutation.variables === portal.key;
                return (
                  <div
                    key={portal.key}
                    className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1 text-xs shadow-2xs"
                  >
                    <span className="font-medium text-ink">{portal.label}</span>
                    <span className="font-mono text-[10px] text-ink-faint">
                      {info ? `(${info.totalOffers} offres)` : ''}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-1.5 text-xs text-herb-800 hover:bg-herb-50"
                      disabled={isRunning}
                      onClick={() => crawlMutation.mutate(portal.key)}
                      title={`Lancer un crawl immédiat de ${portal.label}`}
                    >
                      {isRunning ? (
                        <RefreshCw className="h-3 w-3 animate-spin" />
                      ) : (
                        <Play className="h-3 w-3 fill-herb-700 text-herb-700" />
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </PanelBody>
      </Panel>

      {/* Quick Filter Badges / Navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setApprovedOnly(false);
              setMissingPriceOnly(false);
              setVatOnly(false);
              setHasOffersOnly(false);
              setPage(1);
            }}
            className={cn(
              'px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer flex items-center gap-1.5',
              !approvedOnly && !missingPriceOnly && !vatOnly && !hasOffersOnly
                ? 'bg-herb-700 text-white border-herb-800 shadow-xs'
                : 'bg-card text-ink-soft border-border hover:bg-cream',
            )}
          >
            Tous les produits
            <span className="font-mono text-[11px] opacity-80">({stats?.totalProducts ?? '—'})</span>
          </button>

          <div
            className="px-3 py-1.5 rounded-xl text-xs font-bold border border-emerald-300 bg-emerald-100/80 text-emerald-950 flex items-center gap-1.5 shadow-2xs"
            title="Tous les produits affichés sur cet écran sont validés à 100% en Couche 2 (Final EAN Match)"
          >
            <CheckCheck className="h-3.5 w-3.5 text-emerald-700" />
            100% EAN Validés (Couche 2)
            <span className="font-mono text-[11px] font-semibold text-emerald-800">
              ({stats?.totalProducts ?? stats?.approvedGtinCount ?? '—'})
            </span>
          </div>

          <button
            type="button"
            onClick={() => {
              setMissingPriceOnly(!missingPriceOnly);
              setPage(1);
            }}
            className={cn(
              'px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer flex items-center gap-1.5',
              missingPriceOnly
                ? 'bg-amber-600 text-white border-amber-700 shadow-xs'
                : 'bg-card text-amber-900 border-amber-300 hover:bg-amber-50',
            )}
          >
            <AlertCircle className="h-3.5 w-3.5" />
            Sans prix d'achat
            <span className="font-mono text-[11px] opacity-80">({stats?.missingPurchasePriceCount ?? '—'})</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setHasOffersOnly(!hasOffersOnly);
              setPage(1);
            }}
            className={cn(
              'px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer flex items-center gap-1.5',
              hasOffersOnly
                ? 'bg-sky-700 text-white border-sky-800 shadow-xs'
                : 'bg-card text-sky-800 border-sky-300 hover:bg-sky-50',
            )}
          >
            <Sparkles className="h-3.5 w-3.5" />
            Avec offre grossiste
            <span className="font-mono text-[11px] opacity-80">({stats?.matchedWithWholesalerCount ?? '—'})</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setVatOnly(!vatOnly);
              setPage(1);
            }}
            className={cn(
              'px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer flex items-center gap-1.5',
              vatOnly
                ? 'bg-rose-700 text-white border-rose-800 shadow-xs'
                : 'bg-card text-rose-800 border-rose-300 hover:bg-rose-50',
            )}
          >
            Anomalies TVA
            <span className="font-mono text-[11px] opacity-80">({stats?.vatDiscrepanciesCount ?? '0'})</span>
          </button>
        </div>

        {adoptableItems.length > 0 && (
          <Button
            variant="primary"
            size="sm"
            disabled={adoptBulkMutation.isPending}
            onClick={() => {
              adoptBulkMutation.mutate(
                adoptableItems.map((r) => ({
                  productId: r.product.id,
                  offerId: r.buyBox!.offerId,
                  priceHt: r.buyBox!.unitPriceRealHt ?? r.buyBox!.priceHt,
                  sourceKey: r.buyBox!.sourceKey,
                }))
              );
            }}
            className="shadow-xs font-bold cursor-pointer"
          >
            <CheckCheck className={cn('h-3.5 w-3.5 mr-1', adoptBulkMutation.isPending && 'animate-spin')} />
            {adoptBulkMutation.isPending
              ? 'Adoption en cours…'
              : `⚡ Tout adopter (${adoptableItems.length} offres Buy-Box sur cette page)`}
          </Button>
        )}
      </div>

      {/* Search & Filter Toolbar */}
      <Panel>
        <PanelBody className="py-3.5 px-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-[240px]">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-ink-ghost" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Rechercher nom, SKU, code EAN…"
                  className="w-full rounded-lg border border-border bg-card pl-9 pr-3 py-1.5 text-xs text-ink placeholder:text-ink-ghost focus:border-herb-600 focus:outline-none"
                />
              </div>

              <input
                type="text"
                value={brand}
                onChange={(e) => {
                  setBrand(e.target.value);
                  setPage(1);
                }}
                placeholder="Filtrer par marque…"
                className="w-36 rounded-lg border border-border bg-card px-3 py-1.5 text-xs text-ink placeholder:text-ink-ghost focus:border-herb-600 focus:outline-none"
              />

              <div className="flex items-center gap-1.5 text-xs text-ink-soft">
                <span className="font-medium text-ink-ghost">Tri :</span>
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value as 'recent_approved' | 'missing_price' | 'name' | 'id');
                    setPage(1);
                  }}
                  className="rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs text-ink focus:border-herb-600 focus:outline-none"
                >
                  <option value="recent_approved">Récents validés EAN (Final EAN)</option>
                  <option value="missing_price">Sans prix d'achat en premier</option>
                  <option value="name">Nom du produit (A-Z)</option>
                  <option value="id">Identifiant (#)</option>
                </select>
              </div>

              <label className="flex items-center gap-1.5 text-xs font-medium text-ink cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={vatOnly}
                  onChange={(e) => {
                    setVatOnly(e.target.checked);
                    setPage(1);
                  }}
                  className="rounded border-border text-herb-700 focus:ring-herb-600"
                />
                <span>Anomalies TVA uniquement</span>
              </label>

              <label className="flex items-center gap-1.5 text-xs font-medium text-ink cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={hasOffersOnly}
                  onChange={(e) => {
                    setHasOffersOnly(e.target.checked);
                    setPage(1);
                  }}
                  className="rounded border-border text-herb-700 focus:ring-herb-600"
                />
                <span>Avec offre grossiste</span>
              </label>
            </div>

            <div className="text-xs text-ink-ghost">
              Page {pagination?.page ?? 1} / {pagination?.totalPages ?? 1} ({pagination?.total ?? 0} produits)
            </div>
          </div>
        </PanelBody>
      </Panel>

      {/* Main Reconciliation Table */}
      {reconciliationQuery.isLoading ? (
        <Loading label="Chargement du rapprochement prix & TVA…" />
      ) : items.length === 0 ? (
        <Panel>
          <PanelBody className="py-12 text-center">
            <Info className="h-8 w-8 mx-auto text-ink-ghost mb-2" />
            <p className="text-sm font-semibold text-ink">Aucun produit ne correspond aux filtres.</p>
            <p className="text-xs text-ink-faint mt-1">
              Essayez d'ajuster votre recherche ou décochez les filtres d'anomalies.
            </p>
          </PanelBody>
        </Panel>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-xs">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-border bg-paper/60 text-ink-ghost text-[11px] uppercase tracking-wider font-semibold">
                <th className="py-3 px-3.5 min-w-[280px]">Produit MaPara (Catalogue)</th>
                <th className="py-3 px-2 text-right w-28 whitespace-nowrap">Prix Vente</th>
                <th className="py-3 px-3 min-w-[240px]">Offres Grossistes (Par EAN)</th>
                <th className="py-3 px-3 min-w-[200px]">Buy-Box Grossiste</th>
                <th className="py-3 px-3 text-right w-28 whitespace-nowrap">Marge Brute Réelle</th>
                <th className="py-3 px-3 text-center whitespace-nowrap">Statut TVA</th>
                <th className="py-3 px-3 text-center whitespace-nowrap">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {items.map((row) => {
                const prod = row.product;
                const buyBox = row.buyBox;
                const margin = row.margin;
                const vat = row.vatStatus;

                return (
                  <tr key={prod.id} className="hover:bg-cream/40 transition-colors">
                    {/* Product Identity with Bigger Product Image */}
                    <td className="py-3 px-3.5 max-w-[320px]">
                      <div className="flex items-start gap-3">
                        {prod.image ? (
                          <a
                            href={prod.image}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group/img relative shrink-0 block"
                            title="Cliquer pour afficher l'image en grand"
                          >
                            <img
                              src={prod.image}
                              alt=""
                              className="h-20 w-20 min-w-20 min-h-20 rounded-lg object-contain bg-white border border-border p-1 shadow-2xs transition-transform group-hover/img:scale-105"
                            />
                          </a>
                        ) : (
                          <div className="h-20 w-20 min-w-20 min-h-20 shrink-0 rounded-lg bg-cream/70 border border-border grid place-items-center text-ink-ghost text-xs font-bold">
                            IMG
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-ink leading-tight break-words text-xs" title={prod.name}>
                            {prod.name}
                          </p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5 font-mono text-[10px] text-ink-ghost">
                            <span className="font-semibold text-ink-soft">{prod.sku}</span>
                            {prod.brand && <span>· {prod.brand}</span>}
                            {prod.gtin && (
                              <span className="bg-emerald-50 text-emerald-800 px-1 py-0.2 rounded border border-emerald-400 font-bold inline-flex items-center gap-0.5">
                                <Check className="h-2.5 w-2.5 text-emerald-700" /> {prod.gtin}
                              </span>
                            )}
                            <Link
                              to={`/final-ean-match?q=${encodeURIComponent(prod.gtin || prod.sku || prod.name)}`}
                              className="text-[10px] font-semibold text-herb-800 hover:text-herb-950 hover:underline inline-flex items-center gap-0.5 ml-1"
                              title="Vérifier sur la page Final EAN Match (S23)"
                            >
                              Final EAN (S23) →
                            </Link>
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* MaPara Selling Price & Current Purchase Price (compact width) */}
                    <td className="py-3 px-2 text-right whitespace-nowrap w-28">
                      <div className="font-bold text-ink text-xs">
                        {formatTnd(prod.sellingPriceTtc)} <span className="text-[9px] text-ink-ghost font-normal">TTC</span>
                      </div>
                      <div className="text-[10px] text-ink-ghost leading-tight">
                        {formatTnd(prod.sellingPriceHt)} HT
                      </div>
                      <div className="text-[9px] text-ink-faint">
                        (TVA {prod.currentVatRate ?? 19}%)
                      </div>
                      {prod.currentPurchasePriceHt !== null ? (
                        <div className="text-[10px] text-ink-faint mt-1 pt-1 border-t border-border/40" title={`Fournisseur actuel: ${prod.currentPurchaseSourceKey}`}>
                          Achat : <strong className="text-ink">{formatTnd(prod.currentPurchasePriceHt)}</strong>
                        </div>
                      ) : (
                        <div className="mt-1">
                          <span className="inline-flex items-center gap-0.5 rounded bg-amber-50 border border-amber-300 px-1 py-0.5 text-[9px] font-bold text-amber-900">
                            <AlertCircle className="h-2.5 w-2.5 text-amber-600" />
                            Sans PA
                          </span>
                        </div>
                      )}
                    </td>

                    {/* Wholesaler Offers by EAN with prominent promos */}
                    <td className="py-3 px-3 max-w-[280px]">
                      {row.wholesalerOffers.length === 0 ? (
                        <div className="flex flex-col gap-1.5 items-start">
                          <span className="text-ink-ghost italic text-[11px]">Aucune offre grossiste</span>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <button
                              type="button"
                              onClick={() => openManualMatch(row)}
                              className="inline-flex items-center gap-1 text-[11px] font-bold text-herb-700 hover:text-herb-900 hover:underline cursor-pointer bg-herb-50 hover:bg-herb-100/80 border border-herb-300/60 rounded px-1.5 py-0.5 transition-colors"
                              title="Rechercher et associer manuellement une offre dépôt ou grossiste sans code-barres"
                            >
                              <Search size={11} /> Lier offre dépôt/grossiste
                            </button>
                            <button
                              type="button"
                              onClick={() => handleHideProduct(prod.id, prod.name)}
                              disabled={hideMutation.isPending}
                              className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-700 hover:text-rose-900 hover:underline cursor-pointer bg-rose-50 hover:bg-rose-100/80 border border-rose-300/60 rounded px-1.5 py-0.5 transition-colors"
                              title="Produit discontinué sans équivalent dépôt : masquer du catalogue et du site web"
                            >
                              <EyeOff size={11} /> Masquer (discontinué)
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-1.5">
                          {row.wholesalerOffers.map((offer) => {
                            const isBuyBox = buyBox?.offerId === offer.offerId;
                            const hasPromoDeal =
                              (offer.promoBuyQty !== null && offer.promoFreeQty !== null) ||
                              Boolean(offer.promoPercent) ||
                              Boolean(offer.isPromo);
                            return (
                              <div
                                key={offer.offerId}
                                className={`flex flex-col gap-1 rounded-md px-2 py-1.5 text-[11px] border ${
                                  isBuyBox
                                    ? 'bg-herb-50/90 border-herb-600/40 shadow-2xs ring-1 ring-herb-500/20'
                                    : 'bg-card border-border'
                                }`}
                              >
                                <div className="flex items-center justify-between gap-1.5">
                                  <div className="flex items-center gap-1.5 min-w-0 truncate">
                                    <span
                                      className={`h-2 w-2 rounded-full shrink-0 ${
                                        offer.inStock === true
                                          ? 'bg-emerald-600'
                                          : offer.inStock === false
                                            ? 'bg-rose-500'
                                            : 'bg-amber-400'
                                      }`}
                                      title={offer.inStock === true ? 'En stock' : offer.inStock === false ? 'Rupture' : 'Stock inconnu'}
                                    />
                                    <span className="font-semibold text-ink-soft truncate">{offer.sourceLabel}</span>
                                    {offer.isManualMatch && (
                                      <span
                                        className="text-[9px] font-bold text-purple-700 bg-purple-50 border border-purple-200 rounded px-1 shrink-0"
                                        title="Rapproché manuellement par un opérateur"
                                      >
                                        Manuel
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0 font-mono">
                                    <span className="font-bold text-ink">
                                      {formatTnd(offer.priceHt)} <span className="text-[9px] text-ink-ghost font-normal">HT</span>
                                    </span>
                                    {offer.vatRate !== null && (
                                      <span className="text-[10px] text-ink-ghost font-normal">({offer.vatRate}%)</span>
                                    )}
                                  </div>
                                </div>

                                {/* Promo / Quantity Deal badge */}
                                {hasPromoDeal && (
                                  <div className="flex items-center justify-between gap-1.5 pt-1 border-t border-border/50 font-mono text-[10px]">
                                    <div className="flex items-center gap-1">
                                      {offer.promoBuyQty !== null && offer.promoFreeQty !== null ? (
                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded font-bold bg-amber-100 text-amber-900 border border-amber-300">
                                          🎁 {offer.promoBuyQty}+{offer.promoFreeQty}
                                        </span>
                                      ) : offer.promoPercent ? (
                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded font-bold bg-sky-100 text-sky-900 border border-sky-300">
                                          🏷️ -{offer.promoPercent}%
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded font-bold bg-purple-100 text-purple-900 border border-purple-300">
                                          🔥 Promo
                                        </span>
                                      )}
                                    </div>
                                    {offer.unitPriceRealHt !== null && offer.unitPriceRealHt !== offer.priceHt && (
                                      <span className="text-emerald-700 font-bold" title="Prix unitaire d'achat réel après déduction de la gratuité/remise">
                                        ≈ {formatTnd(offer.unitPriceRealHt)}/u
                                      </span>
                                    )}
                                  </div>
                                )}

                                {offer.isManualMatch && (
                                  <div className="flex items-center justify-end pt-0.5 border-t border-border/40">
                                    <button
                                      type="button"
                                      onClick={() => unlinkOfferMutation.mutate({ productId: row.product.id, offerId: offer.offerId })}
                                      className="text-[9px] text-rose-600 hover:text-rose-800 hover:underline cursor-pointer"
                                      title="Délier cette offre associée manuellement"
                                    >
                                      Délier
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          <button
                            type="button"
                            onClick={() => openManualMatch(row)}
                            className="inline-flex items-center gap-1 text-[10px] text-ink-soft hover:text-herb-800 hover:underline pt-0.5 cursor-pointer"
                            title="Associer une autre offre dépôt ou grossiste"
                          >
                            <Plus size={10} /> Associer une autre offre
                          </button>
                        </div>
                      )}
                    </td>

                    {/* Wholesaler Buy-Box (Priorité grossiste & Promo réelle) */}
                    <td className="py-3 px-3 whitespace-nowrap">
                      {buyBox ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-1">
                            <Sparkles className="h-3 w-3 text-amber-500 shrink-0" />
                            <span className="font-bold text-ink">{buyBox.sourceLabel}</span>
                          </div>
                          <div className="flex items-center gap-1.5 font-mono">
                            <span className="text-herb-900 font-bold text-xs">
                              {formatTnd(buyBox.priceHt)} HT
                            </span>
                            {buyBox.promoBuyQty !== null && buyBox.promoFreeQty !== null ? (
                              <span
                                className="inline-block text-[9px] font-bold px-1.5 py-0.2 rounded bg-amber-100 text-amber-900 border border-amber-300"
                                title={`Offre quantité : payez ${buyBox.promoBuyQty}, ${buyBox.promoFreeQty} offerte(s)`}
                              >
                                🎁 {buyBox.promoBuyQty}+{buyBox.promoFreeQty}
                              </span>
                            ) : buyBox.promoPercent ? (
                              <span className="inline-block text-[9px] font-bold px-1.5 py-0.2 rounded bg-sky-100 text-sky-900 border border-sky-300">
                                🏷️ -{buyBox.promoPercent}%
                              </span>
                            ) : buyBox.isPromo ? (
                              <span className="inline-block text-[9px] font-bold px-1.5 py-0.2 rounded bg-purple-100 text-purple-900 border border-purple-300">
                                🔥 Promo
                              </span>
                            ) : null}
                          </div>
                          {buyBox.unitPriceRealHt !== null && buyBox.unitPriceRealHt !== buyBox.priceHt && (
                            <div className="font-mono text-[10px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-300 rounded px-1.5 py-0.5 inline-block">
                              Coût réel : {formatTnd(buyBox.unitPriceRealHt)} HT/u
                            </div>
                          )}
                          <div>
                            <span
                              className={`inline-block text-[9px] font-semibold px-1 py-0.2 rounded ${
                                buyBox.inStock === true
                                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-600/20'
                                  : buyBox.inStock === false
                                    ? 'bg-rose-50 text-rose-800 border border-rose-600/20'
                                    : 'bg-paper text-ink-ghost'
                              }`}
                            >
                              {buyBox.inStock === true ? 'En stock' : buyBox.inStock === false ? 'Rupture' : 'Stock inconnu'}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <span className="text-ink-ghost">—</span>
                      )}
                    </td>

                    {/* Gross Markup (target 20% on real purchase cost) */}
                    <td className="py-3 px-3 text-right whitespace-nowrap font-mono">
                      {margin.grossMarkupPercent !== null ? (
                        <div>
                          <div className="flex items-center justify-end gap-1 font-bold text-xs">
                            {margin.status === 'below_target' ? (
                              <TrendingDown className="h-3.5 w-3.5 text-rose-600" />
                            ) : (
                              <TrendingUp
                                className={
                                  margin.status === 'above_target'
                                    ? 'h-3.5 w-3.5 text-sky-600'
                                    : 'h-3.5 w-3.5 text-emerald-600'
                                }
                              />
                            )}
                            <span
                              className={
                                margin.status === 'below_target'
                                  ? 'text-rose-700 font-bold'
                                  : margin.status === 'on_target'
                                    ? 'text-emerald-700 font-bold'
                                    : 'text-sky-700 font-bold'
                              }
                              title={
                                margin.status === 'below_target'
                                  ? 'Markup sous l’objectif de 20%'
                                  : margin.status === 'on_target'
                                    ? 'Markup sur l’objectif de 20%'
                                    : 'Markup au-dessus de l’objectif de 20%'
                              }
                            >
                              {margin.grossMarkupPercent > 0 ? '+' : ''}
                              {margin.grossMarkupPercent}%
                            </span>
                          </div>
                          <div className="text-[10px] text-ink-ghost mt-0.5">
                            {formatTnd(margin.grossMarginAmount)}
                          </div>
                          {buyBox?.unitPriceRealHt && buyBox.unitPriceRealHt !== buyBox.priceHt && (
                            <div className="text-[9px] text-emerald-700 font-medium mt-0.5" title="Marge brute calculée sur le prix unitaire réel après déduction de la promotion">
                              (sur coût réel promo)
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-ink-ghost">—</span>
                      )}
                    </td>

                    {/* VAT verification — « Conforme » only when a wholesaler offer carried a rate */}
                    <td className="py-3 px-3 text-center whitespace-nowrap">
                      {vat.hasVatMismatch ? (
                        <div
                          className="inline-flex items-center gap-1 bg-amber-50 border border-amber-600/30 text-amber-900 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                          title={vat.vatMismatchNote ?? 'Anomalie de TVA'}
                        >
                          <AlertCircle className="h-3 w-3 text-amber-700 shrink-0" />
                          <span>{vat.vatMismatchNote}</span>
                        </div>
                      ) : vat.vatVerified ? (
                        <div className="inline-flex items-center gap-1 text-emerald-700 text-[10px] font-medium">
                          <CheckCircle2 className="h-3 w-3" />
                          <span>Conforme ({vat.currentVatRate ?? 19}%)</span>
                        </div>
                      ) : (
                        <div
                          className="inline-flex items-center gap-1 text-ink-ghost text-[10px] font-medium"
                          title="Aucune offre grossiste avec TVA — le 19% affiché est le défaut de la fiche, pas une vérification"
                        >
                          <HelpCircle className="h-3 w-3" />
                          <span>Non vérifiée (défaut {vat.currentVatRate ?? 19}%)</span>
                        </div>
                      )}
                    </td>

                    {/* Action: Adopt Buy-Box Price or Hide */}
                    <td className="py-3 px-3 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1.5">
                        {buyBox && (buyBox.unitPriceRealHt ?? buyBox.priceHt) !== prod.currentPurchasePriceHt ? (
                          <Button
                            variant="soft"
                            size="sm"
                            className="text-[11px] h-7 px-2 font-medium"
                            disabled={adoptPriceMutation.isPending || hideMutation.isPending}
                            onClick={() => {
                              const targetPrice = buyBox.unitPriceRealHt ?? buyBox.priceHt;
                              adoptPriceMutation.mutate({
                                productId: prod.id,
                                offerId: buyBox.offerId,
                                priceHt: targetPrice,
                                sourceKey: buyBox.sourceKey,
                              });
                            }}
                            title={`Adopter le prix d'achat grossiste ${buyBox.sourceLabel} (${formatTnd(buyBox.unitPriceRealHt ?? buyBox.priceHt)})`}
                          >
                            {buyBox.unitPriceRealHt && buyBox.unitPriceRealHt !== buyBox.priceHt
                              ? `Adopter (${formatTnd(buyBox.unitPriceRealHt)})`
                              : 'Adopter Prix'}
                          </Button>
                        ) : buyBox ? (
                          <span className="text-[10px] text-emerald-700 font-semibold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-600/20">
                            Prix Actif
                          </span>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-[11px] h-7 px-2 font-medium"
                            disabled={hideMutation.isPending}
                            onClick={() => openManualMatch(row)}
                            title="Rechercher une offre dépôt ou grossiste sans code-barres"
                          >
                            <Search size={11} className="mr-1" /> Lier
                          </Button>
                        )}

                        <button
                          type="button"
                          onClick={() => handleHideProduct(prod.id, prod.name)}
                          disabled={hideMutation.isPending || adoptPriceMutation.isPending}
                          className="inline-flex items-center gap-1 px-2 py-1 h-7 rounded-lg border border-border bg-white hover:bg-rose-50 text-ink-muted hover:text-rose-700 hover:border-rose-300 text-[11px] font-semibold transition-colors cursor-pointer shadow-2xs"
                          title="Produit discontinué / abandonné : masquer du catalogue et retirer du site web WooCommerce"
                        >
                          <EyeOff className="h-3 w-3 text-rose-600" />
                          <span>Masquer</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Footer */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border pt-4">
          <div className="text-xs text-ink-ghost">
            Affichage de {(page - 1) * pagination.limit + 1} à{' '}
            {Math.min(page * pagination.limit, pagination.total)} sur {pagination.total} produits
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Précédent
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Suivant
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
      {/* Manual Wholesaler / Depot Match Modal */}
      {manualMatchTarget &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto"
          >
            <div className="relative w-full max-w-4xl max-h-[90vh] flex flex-col rounded-2xl bg-card border border-border shadow-2xl overflow-hidden my-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between border-b border-border px-6 py-4 bg-cream/40">
                <div>
                  <h2 className="text-base font-bold text-ink flex items-center gap-2">
                    <Search className="h-5 w-5 text-herb-700" />
                    Rapprochement manuel Dépôt / Grossiste
                  </h2>
                  <p className="text-xs text-ink-soft mt-0.5">
                    Associez une offre issue des crawls Dépôt ou portails B2B sans code-barres à ce produit.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setManualMatchTarget(null)}
                  className="rounded-lg p-1.5 text-ink-soft hover:bg-cream hover:text-ink cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Product summary card */}
              <div className="p-4 bg-card border-b border-border/80 flex items-center gap-4">
                {manualMatchTarget.product.image ? (
                  <img
                    src={manualMatchTarget.product.image}
                    alt=""
                    className="h-16 w-16 min-h-16 min-w-16 rounded-xl object-contain bg-white border border-border p-1 shadow-2xs"
                  />
                ) : (
                  <div className="h-16 w-16 min-h-16 min-w-16 rounded-xl bg-cream border border-border grid place-items-center text-xs font-bold text-ink-ghost">
                    IMG
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h3 className="font-bold text-ink text-sm leading-snug line-clamp-2">
                    {manualMatchTarget.product.name}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mt-1.5 font-mono text-[11px] text-ink-ghost">
                    <span className="font-semibold text-ink-soft">SKU: {manualMatchTarget.product.sku}</span>
                    {manualMatchTarget.product.brand && (
                      <span>
                        · Marque : <strong className="text-ink">{manualMatchTarget.product.brand}</strong>
                      </span>
                    )}
                    {manualMatchTarget.product.gtin && (
                      <span className="bg-emerald-50 text-emerald-800 px-1.5 py-0.2 rounded border border-emerald-300 font-bold inline-flex items-center gap-1">
                        <Check size={11} /> {manualMatchTarget.product.gtin}
                      </span>
                    )}
                    <span className="ml-auto font-sans text-xs flex items-center gap-2">
                      <span>
                        Vente :{' '}
                        <strong className="text-ink font-mono">
                          {formatTnd(manualMatchTarget.product.sellingPriceTtc)} TTC
                        </strong>
                      </span>
                      {manualMatchTarget.product.currentPurchasePriceHt !== null ? (
                        <span className="ml-2 text-ink-soft">
                          · Achat actuel :{' '}
                          <strong className="font-mono text-ink">
                            {formatTnd(manualMatchTarget.product.currentPurchasePriceHt)} HT
                          </strong>
                        </span>
                      ) : (
                        <span className="ml-2 text-amber-700 bg-amber-50 border border-amber-200 px-1 rounded text-[10px] font-bold">
                          Sans PA
                        </span>
                      )}

                      <button
                        type="button"
                        onClick={() => {
                          const target = manualMatchTarget;
                          setManualMatchTarget(null);
                          handleHideProduct(target.product.id, target.product.name);
                        }}
                        disabled={hideMutation.isPending}
                        className="ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-rose-300 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-bold transition-colors cursor-pointer shadow-2xs shrink-0"
                        title="Produit discontinué sans équivalent dépôt : masquer du catalogue et retirer du site web WooCommerce"
                      >
                        <EyeOff size={13} className="text-rose-600" />
                        <span>Masquer (Discontinué)</span>
                      </button>
                    </span>
                  </div>
                </div>
              </div>

              {/* Search controls */}
              <div className="p-4 bg-cream/20 border-b border-border space-y-3">
                <div className="flex flex-col sm:flex-row gap-2.5 items-stretch">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-ghost" />
                    <input
                      type="text"
                      value={manualSearchQuery}
                      onChange={(e) => setManualSearchQuery(e.target.value)}
                      placeholder="Rechercher par nom de produit, marque, mots-clés (ex: Apigorge, Pediakids)..."
                      className="w-full pl-9 pr-8 py-2 rounded-xl border border-border bg-card text-xs text-ink placeholder:text-ink-ghost focus:outline-hidden focus:ring-2 focus:ring-herb-600 font-medium"
                    />
                    {manualSearchQuery && (
                      <button
                        type="button"
                        onClick={() => setManualSearchQuery('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-ghost hover:text-ink cursor-pointer"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  <select
                    value={manualSearchSource}
                    onChange={(e) => setManualSearchSource(e.target.value)}
                    className="px-3 py-2 rounded-xl border border-border bg-card text-xs font-semibold text-ink focus:outline-hidden focus:ring-2 focus:ring-herb-600"
                  >
                    <option value="all">Toutes les sources (Dépôt & Grossistes)</option>
                    {manualSearchQueryResults.data?.sources.map((s) => (
                      <option key={s.key} value={s.key}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Quick search chips */}
                {searchChips.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap pt-1">
                    <span className="text-[10px] text-ink-ghost font-semibold uppercase tracking-wider mr-1">
                      Mots-clés rapides :
                    </span>
                    {searchChips.map((chip, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setManualSearchQuery(chip)}
                        className={cn(
                          'text-[11px] rounded-lg px-2 py-0.5 border transition-colors cursor-pointer',
                          manualSearchQuery.trim().toLowerCase() === chip.trim().toLowerCase()
                            ? 'bg-herb-100 text-herb-900 border-herb-400 font-bold'
                            : 'bg-card text-ink-soft border-border hover:bg-cream hover:text-ink'
                        )}
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Results Body */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[260px] max-h-[50vh]">
                {manualSearchQueryResults.isLoading ? (
                  <div className="py-12 flex flex-col items-center justify-center text-ink-soft gap-2">
                    <RefreshCw className="h-6 w-6 animate-spin text-herb-700" />
                    <p className="text-xs">Recherche dans les catalogues dépôt & grossistes...</p>
                  </div>
                ) : manualSearchQuery.trim().length < 2 ? (
                  <div className="py-12 text-center text-ink-ghost text-xs">
                    Saisissez au moins 2 caractères pour lancer la recherche.
                  </div>
                ) : (manualSearchQueryResults.data?.items.length ?? 0) === 0 ? (
                  <div className="py-12 text-center space-y-2">
                    <p className="text-xs font-bold text-ink">Aucune offre trouvée pour « {manualSearchQuery} »</p>
                    <p className="text-[11px] text-ink-soft max-w-md mx-auto">
                      Essayez de rechercher avec moins de mots (par exemple juste la marque ou les 2 premiers mots sans la contenance).
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between text-[11px] text-ink-ghost font-semibold px-1">
                      <span>
                        {manualSearchQueryResults.data?.total} offre(s) trouvée(s)
                      </span>
                      <span>Associez l'offre correspondante pour synchroniser les prix</span>
                    </div>

                    {manualSearchQueryResults.data?.items.map((offer) => {
                      const isMatched = offer.isMatchedToThisProduct;
                      return (
                        <div
                          key={offer.id}
                          className={cn(
                            'rounded-xl border p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-colors',
                            isMatched
                              ? 'border-purple-300 bg-purple-50/40 ring-1 ring-purple-400/30'
                              : 'border-border bg-card hover:bg-cream/20'
                          )}
                        >
                          <div className="min-w-0 flex-1 space-y-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="rounded bg-sky-100 text-sky-900 border border-sky-300 px-1.5 py-0.2 text-[10px] font-bold">
                                {offer.sourceLabel}
                              </span>
                              {offer.referenceRaw && (
                                <span className="font-mono text-[10px] text-ink-soft bg-card border border-border px-1 py-0.2 rounded">
                                  Réf: {offer.referenceRaw}
                                </span>
                              )}
                              {offer.gtinRaw && (
                                <span className="font-mono text-[10px] text-emerald-800 bg-emerald-50 border border-emerald-300 px-1 py-0.2 rounded">
                                  EAN: {offer.gtinRaw}
                                </span>
                              )}
                              <span
                                className={cn(
                                  'text-[10px] px-1 py-0.2 rounded font-medium',
                                  offer.inStock === true
                                    ? 'text-emerald-700 bg-emerald-50'
                                    : offer.inStock === false
                                    ? 'text-rose-700 bg-rose-50'
                                    : 'text-amber-700 bg-amber-50'
                                )}
                              >
                                {offer.inStock === true ? 'En stock' : offer.inStock === false ? 'Rupture' : 'Stock ?'}
                              </span>
                            </div>

                            <h4 className="text-xs font-bold text-ink leading-snug line-clamp-2">
                              {offer.nameRaw}
                            </h4>

                            <div className="flex items-center gap-3 text-xs pt-0.5">
                              <span className="font-mono">
                                Prix HT : <strong className="text-ink">{formatTnd(offer.priceHt)}</strong>
                                {offer.vatRate !== null && (
                                  <span className="text-[10px] text-ink-ghost ml-1">({offer.vatRate}%)</span>
                                )}
                              </span>
                              {offer.unitPriceRealHt !== null && offer.unitPriceRealHt !== offer.priceHt && (
                                <span className="text-[11px] font-mono text-emerald-700 font-bold">
                                  Prix réel : {formatTnd(offer.unitPriceRealHt)}
                                </span>
                              )}
                              {offer.promoBuyQty && offer.promoFreeQty && (
                                <span className="text-[10px] font-bold bg-amber-100 text-amber-900 border border-amber-300 px-1 rounded">
                                  🎁 {offer.promoBuyQty}+{offer.promoFreeQty}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                            {isMatched ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-purple-800 bg-purple-100 border border-purple-300 px-2 py-1 rounded-lg inline-flex items-center gap-1">
                                  <Check size={12} /> Lié à ce produit
                                </span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    unlinkOfferMutation.mutate({
                                      productId: manualMatchTarget.product.id,
                                      offerId: offer.id,
                                    })
                                  }
                                  disabled={unlinkOfferMutation.isPending}
                                  className="text-xs text-rose-600 hover:text-rose-800"
                                >
                                  Délier
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                <Button
                                  size="sm"
                                  variant="soft"
                                  onClick={() =>
                                    matchOfferMutation.mutate({
                                      productId: manualMatchTarget.product.id,
                                      offerId: offer.id,
                                      adoptPrice: false,
                                    })
                                  }
                                  disabled={matchOfferMutation.isPending}
                                  className="text-xs"
                                >
                                  Lier uniquement
                                </Button>
                                <Button
                                  size="sm"
                                  variant="primary"
                                  onClick={() =>
                                    matchOfferMutation.mutate({
                                      productId: manualMatchTarget.product.id,
                                      offerId: offer.id,
                                      adoptPrice: true,
                                      adoptVat: true,
                                    })
                                  }
                                  disabled={matchOfferMutation.isPending}
                                  className="text-xs font-bold"
                                >
                                  Lier & Adopter PA/TVA
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-border bg-cream/30 flex items-center justify-between">
                <p className="text-[11px] text-ink-soft">
                  Astuce : Si le dépôt ou grossiste nomme le produit différemment, essayez de rechercher uniquement la marque ou les mots principaux.
                </p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setManualMatchTarget(null)}
                >
                  Fermer
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
